import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Plus, Trash2, Loader2, Pencil, ExternalLink, Copy, CalendarDays, Camera, Video } from "lucide-react";

const CATEGORIES = [
  { v: "dance_show", l: "Dance Show" },
  { v: "dance_competition", l: "Dance Competition" },
  { v: "event", l: "Event" },
];
const VISIBILITY = [
  { v: "public", l: "Public (listed on store)" },
  { v: "hidden", l: "Hidden (unlisted, link only)" },
  { v: "private", l: "Private (private link only)" },
];

const emptyEvent = () => ({
  name: "",
  short_name: "",
  category: "event",
  description: "",
  short_description: "",
  visibility: "public",
  password: "",
  thumbnail_url: "",
  field_name: { enabled: false, label: "Dancer name" },
  field_number: { enabled: false, label: "Competitor number" },
  field_studio: { enabled: false, label: "Studio" },
  field_class: { enabled: false, label: "Class" },
  performance_mode: "manual",
  performances: [],
  content_types: [],
  competitor_mode: "flexible",
  default_media_price: 0,
  photo_price: 0,
  video_price: 0,
  package_price: 0,
  start_date: "",
  end_date: "",
  pre_purchase_required: false,
  livestream: false,
  storefront_badges: [],
  image_badge: "",
  gallery: [],
  delivery_download: true,
  delivery_usb: false,
  usb_fee: 0,
  card_surcharge_enabled: true,
});

export default function AdminProducts() {
  const [events, setEvents] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    setEvents((p) => p.filter((e) => e.id !== id));
    try { await api.delete(`/admin/events/${id}`); toast.success("Product deleted"); } catch { load(); }
  };

  if (events === null) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Products</div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Products &amp; events</h1>
        </div>
        <Button data-testid="add-product" onClick={() => setEditing(emptyEvent())} className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
          <Plus className="mr-1.5 h-4 w-4" /> New product
        </Button>
      </div>

      {events.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 py-20 text-center text-zinc-500">
          <CalendarDays className="mx-auto mb-3 h-8 w-8" /> No products yet. Create your first event.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {events.map((e) => (
            <div key={e.id} data-testid={`product-card-${e.id}`} className="overflow-hidden rounded-xl border border-zinc-800 bg-[#0d0d10]">
              <div className="relative aspect-[16/9] bg-zinc-950">
                {e.thumbnail_url && <img src={e.thumbnail_url} alt={e.name} className="h-full w-full object-cover" />}
                <span className="absolute left-3 top-3 rounded-full border border-zinc-700 bg-black/50 px-2.5 py-0.5 text-xs capitalize text-zinc-200">
                  {(CATEGORIES.find((c) => c.v === e.category) || {}).l}
                </span>
                <span className="absolute right-3 top-3 rounded-full border border-zinc-700 bg-black/50 px-2.5 py-0.5 text-xs capitalize text-zinc-300">{e.visibility}</span>
              </div>
              <div className="space-y-2 p-4">
                <h3 className="font-display text-lg font-bold text-white">{e.name}</h3>
                <p className="text-xs text-zinc-500">{e.media_count} media attached{e.password ? " · password-protected" : ""}</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button data-testid={`edit-product-${e.id}`} size="sm" onClick={() => setEditing(e)} className="h-8 bg-zinc-800 text-white hover:bg-zinc-700"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/event/${e.id}`); toast.success("Event link copied"); }} className="h-8 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"><Copy className="mr-1 h-3.5 w-3.5" /> Link</Button>
                  <a href={`/event/${e.id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"><ExternalLink className="h-3.5 w-3.5" /></Button></a>
                  <Button data-testid={`delete-product-${e.id}`} size="sm" variant="outline" onClick={() => remove(e.id)} className="h-8 border-rose-500/40 bg-transparent text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ProductDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FieldRow({ label, conf, onChange }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3">
      <Switch data-testid={`field-${label}-enabled`} checked={conf.enabled} onCheckedChange={(c) => onChange({ ...conf, enabled: c })} />
      <span className="w-20 text-xs uppercase tracking-wide text-zinc-500">{label}</span>
      <Input data-testid={`field-${label}-label`} value={conf.label} onChange={(e) => onChange({ ...conf, label: e.target.value })} placeholder="Display label" className="h-8 flex-1 border-zinc-700 bg-zinc-900 text-sm text-white" />
    </div>
  );
}

function ProductDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...emptyEvent(), ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const addPerf = () => set("performances", [...(f.performances || []), { id: crypto.randomUUID(), name: "", studio: "", number: "" }]);
  const updPerf = (i, k, v) => set("performances", f.performances.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const rmPerf = (i) => set("performances", f.performances.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    setBusy(true);
    const payload = {
      name: f.name, short_name: f.short_name, category: f.category, description: f.description, short_description: f.short_description,
      visibility: f.visibility, password: f.password, thumbnail_url: f.thumbnail_url,
      field_name: f.field_name, field_number: f.field_number, field_studio: f.field_studio, field_class: f.field_class,
      performance_mode: f.performance_mode, performances: f.performances, card_surcharge_enabled: f.card_surcharge_enabled,
      content_types: f.content_types, competitor_mode: f.competitor_mode, default_media_price: Number(f.default_media_price) || 0,
      photo_price: Number(f.photo_price) || 0, video_price: Number(f.video_price) || 0, package_price: Number(f.package_price) || 0,
      start_date: f.start_date || null, end_date: f.end_date || null, pre_purchase_required: !!f.pre_purchase_required, livestream: !!f.livestream,
      storefront_badges: f.storefront_badges || [], image_badge: f.image_badge || "",
      gallery: (f.gallery || []).filter(Boolean),
      delivery_download: f.delivery_download, delivery_usb: f.delivery_usb, usb_fee: Number(f.usb_fee) || 0,
    };
    try {
      if (f.id) await api.put(`/admin/events/${f.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Product saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto border-zinc-800 bg-zinc-900 text-white sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">{f.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-zinc-300">Name</Label>
              <Input data-testid="product-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Short name</Label>
              <Input data-testid="product-short-name" value={f.short_name || ""} onChange={(e) => set("short_name", e.target.value)} placeholder="Used in email subjects" className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="product-category" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-zinc-300">Short description</Label>
            <Textarea data-testid="product-short-description" value={f.short_description || ""} onChange={(e) => set("short_description", e.target.value)} rows={3} placeholder="Shown on store cards (max 4 lines). Leave blank to use the full description." className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
          </div>
          <div>
            <Label className="text-zinc-300">Description</Label>
            <div data-testid="product-description" className="quill-dark mt-1 rounded-md border border-zinc-700 bg-zinc-950">
              <ReactQuill theme="snow" value={f.description || ""} onChange={(v) => set("description", v)}
                modules={{ toolbar: [[{ header: [1, 2, 3, false] }], ["bold", "italic", "underline"], [{ color: [] }], ["link"], ["clean"]] }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Visibility</Label>
              <Select value={f.visibility} onValueChange={(v) => set("visibility", v)}>
                <SelectTrigger data-testid="product-visibility" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">{VISIBILITY.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Password (optional)</Label>
              <Input data-testid="product-password" value={f.password || ""} onChange={(e) => set("password", e.target.value)} placeholder="Protects description + media" className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300">Cover image URL</Label>
            <Input data-testid="product-thumb" value={f.thumbnail_url || ""} onChange={(e) => set("thumbnail_url", e.target.value)} placeholder="https://…" className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
          </div>

          <div className="space-y-2 rounded-lg border border-zinc-800 p-3">
            <Label className="text-zinc-300">Image carousel (up to 6 URLs)</Label>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Input key={i} data-testid={`gallery-${i}`} value={(f.gallery || [])[i] || ""} onChange={(e) => { const g = [...(f.gallery || [])]; while (g.length < 6) g.push(""); g[i] = e.target.value; set("gallery", g); }} placeholder={`Image ${i + 1} URL`} className="h-8 border-zinc-700 bg-zinc-950 text-sm text-white" />
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-lg border border-zinc-800 p-3">
            <div><Label className="text-xs uppercase text-zinc-500">Event start date</Label><Input data-testid="product-start" type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
            <div><Label className="text-xs uppercase text-zinc-500">Event end date</Label><Input data-testid="product-end" type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
            <div className="col-span-2 flex items-center justify-between"><span className="text-sm text-zinc-300">Pre-purchase required <span className="text-xs text-zinc-500">(unavailable after end date)</span></span><Switch data-testid="product-prepurchase" checked={!!f.pre_purchase_required} onCheckedChange={(c) => set("pre_purchase_required", c)} /></div>
            <div className="col-span-2 flex items-center justify-between"><span className="text-sm text-zinc-300">Livestream available</span><Switch data-testid="product-livestream" checked={!!f.livestream} onCheckedChange={(c) => set("livestream", c)} /></div>
          </div>

          <div className="space-y-3 rounded-lg border border-zinc-800 p-3">
            <div>
              <Label className="text-zinc-300">Storefront badges</Label>
              <p className="text-xs text-zinc-500">Shown on the store card and on the opened product page.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[{ v: "photos", l: "Photos" }, { v: "video", l: "Video" }, { v: "livestream", l: "Livestream" }, { v: "download", l: "Download" }].map(({ v, l }) => {
                const active = (f.storefront_badges || []).includes(v);
                return (
                  <button key={v} type="button" data-testid={`badge-toggle-${v}`}
                    onClick={() => set("storefront_badges", active ? f.storefront_badges.filter((x) => x !== v) : [...(f.storefront_badges || []), v])}
                    className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${active ? "border-[#ff2a5f] bg-[#ff2a5f]/15 text-[#ff2a5f]" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
                    {l}
                  </button>
                );
              })}
            </div>
            <div>
              <Label className="text-xs uppercase text-zinc-500">Image badge (shown over the product image)</Label>
              <Select value={f.image_badge || "none"} onValueChange={(v) => set("image_badge", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="product-image-badge" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="pre_order">Pre-order</SelectItem>
                  <SelectItem value="instant_download">Instant Download</SelectItem>
                  <SelectItem value="unavailable">No longer available</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {f.category === "dance_competition" && (<>
          <div className="space-y-2 rounded-lg border border-zinc-800 p-3">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Performer fields</Label>
              <span className="text-xs text-zinc-500">Enable & rename per product</span>
            </div>
            <FieldRow label="name" conf={f.field_name} onChange={(v) => set("field_name", v)} />
            <FieldRow label="number" conf={f.field_number} onChange={(v) => set("field_number", v)} />
            <FieldRow label="studio" conf={f.field_studio} onChange={(v) => set("field_studio", v)} />
            <FieldRow label="class" conf={f.field_class} onChange={(v) => set("field_class", v)} />
          </div>

          <div className="space-y-3 rounded-lg border border-zinc-800 p-3">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Performance entry</Label>
              <Select value={f.performance_mode} onValueChange={(v) => set("performance_mode", v)}>
                <SelectTrigger data-testid="product-perf-mode" className="h-8 w-44 border-zinc-700 bg-zinc-950 text-sm text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                  <SelectItem value="manual">Manual entry</SelectItem>
                  <SelectItem value="preset">Pick from preset list</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.performance_mode === "preset" && (
              <div className="space-y-2">
                {(f.performances || []).map((p, i) => (
                  <div key={p.id} className="grid grid-cols-12 gap-2">
                    <Input data-testid={`perf-name-${i}`} value={p.name} onChange={(e) => updPerf(i, "name", e.target.value)} placeholder="Dancer/team" className="col-span-5 h-8 border-zinc-700 bg-zinc-950 text-sm text-white" />
                    <Input data-testid={`perf-studio-${i}`} value={p.studio} onChange={(e) => updPerf(i, "studio", e.target.value)} placeholder="Studio" className="col-span-4 h-8 border-zinc-700 bg-zinc-950 text-sm text-white" />
                    <Input data-testid={`perf-number-${i}`} value={p.number} onChange={(e) => updPerf(i, "number", e.target.value)} placeholder="No." className="col-span-2 h-8 border-zinc-700 bg-zinc-950 text-sm text-white" />
                    <button onClick={() => rmPerf(i)} className="col-span-1 flex items-center justify-center text-zinc-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button data-testid="add-perf" variant="outline" onClick={addPerf} className="w-full border-dashed border-zinc-700 bg-transparent text-zinc-400 hover:text-white"><Plus className="mr-1.5 h-4 w-4" /> Add performance</Button>
              </div>
            )}
          </div>
          </>)}

          <div className="flex items-center justify-between rounded-lg border border-zinc-800 p-3">
            <div>
              <Label className="text-zinc-300">Card payment surcharge</Label>
              <p className="text-xs text-zinc-500">Apply the portal card surcharge to this product</p>
            </div>
            <Switch data-testid="product-surcharge" checked={f.card_surcharge_enabled} onCheckedChange={(c) => set("card_surcharge_enabled", c)} />
          </div>

          {f.category === "dance_competition" && (
            <div className="space-y-3 rounded-lg border border-violet-500/30 bg-violet-500/5 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-200">Competition media</Label>
                <span className="text-xs text-zinc-500">Managed in Performance Manager</span>
              </div>
              <div>
                <Label className="text-xs uppercase text-zinc-500">Content types produced per performance</Label>
                <div className="mt-1.5 flex gap-2">
                  {[{ v: "photos", l: "Photos", Icon: Camera }, { v: "video", l: "Video", Icon: Video }].map(({ v, l, Icon }) => {
                    const active = (f.content_types || []).includes(v);
                    return (
                      <button key={v} type="button" data-testid={`content-type-${v}`}
                        onClick={() => set("content_types", active ? f.content_types.filter((x) => x !== v) : [...(f.content_types || []), v])}
                        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${active ? "border-[#ff2a5f] bg-[#ff2a5f]/15 text-[#ff2a5f]" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
                        <Icon className="h-4 w-4" /> {l}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-zinc-500">One media item is auto-created per performance for each selected type.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-zinc-500">Competitor numbers</Label>
                  <Select value={f.competitor_mode} onValueChange={(v) => set("competitor_mode", v)}>
                    <SelectTrigger data-testid="product-competitor-mode" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                      <SelectItem value="flexible">Flexible (auto by order)</SelectItem>
                      <SelectItem value="fixed">Fixed (assigned manually)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-zinc-500">Photo price</Label>
                  <Input data-testid="product-photo-price" type="number" step="0.01" value={f.photo_price} onChange={(e) => set("photo_price", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-zinc-500">Video price</Label>
                  <Input data-testid="product-video-price" type="number" step="0.01" value={f.video_price} onChange={(e) => set("video_price", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-zinc-500">Package price (both)</Label>
                  <Input data-testid="product-package-price" type="number" step="0.01" value={f.package_price} onChange={(e) => set("package_price", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3 rounded-lg border border-zinc-800 p-3">
            <Label className="text-zinc-300">Delivery options</Label>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-300">Digital download</span>
              <Switch data-testid="delivery-download" checked={f.delivery_download} onCheckedChange={(c) => set("delivery_download", c)} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-300">USB drive (physical fulfilment)</span>
              <Switch data-testid="delivery-usb" checked={f.delivery_usb} onCheckedChange={(c) => set("delivery_usb", c)} />
            </div>
            {f.delivery_usb && (
              <div>
                <Label className="text-xs uppercase text-zinc-500">USB drive fee (NZD)</Label>
                <Input data-testid="usb-fee" type="number" step="0.01" value={f.usb_fee} onChange={(e) => set("usb_fee", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
          <Button data-testid="save-product" onClick={save} disabled={busy} className="bg-[#ff2a5f] text-white hover:bg-[#e01f50]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
